import { SITL_SEAT_SLUG } from '../utils/mapExclusiveSurfaces';
import {
  FOC_FSLTP_TOUR_STEPS,
  type FocFsltpTourStepId,
  type FocFsltpTourSurface,
} from './focFsltpTourSteps';

/**
 * The tour runs outside React, so Layers, SITL Presentation, and Hub SitStat
 * hosts register the UI moves they alone can perform.
 */
export type FocFsltpTourApi = {
  /** Optional; falls back to window.location when omitted. */
  getLocation: () => { pathname: string; search: string };
  showLayers: () => void;
  openSitlPresentation: () => void;
  openHubDaily: () => void;
  closeSidePanels: () => void;
  showOverview: () => void;
  openFirstLayerDetail: () => void;
  expandFirstExternalLayer: () => void;
};

let api: Partial<FocFsltpTourApi> = {};
let pendingStep: FocFsltpTourStepId | null = null;

export function registerFocFsltpTourApi(
  partial: Partial<FocFsltpTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    const needed = surfaceFor(step);
    if (currentSurface() === needed) {
      pendingStep = null;
      prepareFocFsltpStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof FocFsltpTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocFsltpTourApi(): Partial<FocFsltpTourApi> {
  return api;
}

function surfaceFor(id: FocFsltpTourStepId): FocFsltpTourSurface {
  return (
    FOC_FSLTP_TOUR_STEPS.find((step) => step.id === id)?.surface ?? 'layers'
  );
}

function readLocation(): { pathname: string; search: string } {
  const fromHost = getFocFsltpTourApi().getLocation?.();
  if (fromHost) return fromHost;
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
    };
  }
  return { pathname: '/hub', search: '?openTab=Layers' };
}

function currentSurface(): FocFsltpTourSurface {
  const { pathname, search } = readLocation();
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const openTab = params.get('openTab') ?? '';
  if (
    pathname.includes(`/seat/${SITL_SEAT_SLUG}`) &&
    openTab === 'Presentation'
  ) {
    return 'sitlPresentation';
  }
  if (
    (pathname === '/hub' || pathname === '/' || pathname.endsWith('/hub')) &&
    openTab === 'SitStat'
  ) {
    return 'hubDaily';
  }
  return 'layers';
}

function ensureSurface(id: FocFsltpTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();
  if (current === needed) {
    pendingStep = null;
    return true;
  }

  const host = getFocFsltpTourApi();
  pendingStep = id;

  if (needed === 'sitlPresentation') {
    host.openSitlPresentation?.();
    return false;
  }
  if (needed === 'hubDaily') {
    host.openHubDaily?.();
    return false;
  }
  host.showLayers?.();
  return false;
}

export function prepareFocFsltpStep(id: FocFsltpTourStepId) {
  if (!ensureSurface(id)) return;

  const host = getFocFsltpTourApi();
  const surface = surfaceFor(id);

  if (surface === 'sitlPresentation' || surface === 'hubDaily') {
    return;
  }

  switch (id) {
    case 'sectionIntro':
    case 'fsltp001':
    case 'fsltp005':
    case 'cop010':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    case 'fsltp002':
    case 'cop010a':
      host.closeSidePanels?.();
      host.openFirstLayerDetail?.();
      return;
    case 'fsltp003':
    case 'fsltp004':
    case 'fsltp006':
    case 'cop010b':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    case 'cop011':
    case 'focFeedback':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    default:
      host.closeSidePanels?.();
      host.showOverview?.();
  }
}

export function resetFocFsltpTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
